'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { SparkEngine, SparkJob } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { Panels } from '@/components/panels/panels';
import { Table } from '@/components/table';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';
import { useWorkspace as useGlobalWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { dayjs } from '@/utils/dayjs';

import { DeleteSparkEngineDialog } from './deleteSparkEngineDialog';
import { SparkEngineStatusCell } from './sparkEngineStatusCell';

import { eonSparkVersionLabel } from '../eonSparkVersionUtils';
import { createSparkJobsTableColumns } from '../spark-jobs/sparkJobsTableColumns';

interface SparkEnginePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const SparkEnginePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: SparkEnginePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const { currentProjectId } = useUser();
  const { rightPanel } = useGlobalWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: engineData, isLoading: isEngineLoading } =
    dal.lakehouse.useListSparkEngines(workspaceId);

  const { data: jobsData, isLoading: isJobsLoading } =
    dal.lakehouse.useListSparkJobs(workspaceId);

  const engine = engineData?.sparkEngines?.find(
    (e: SparkEngine) => e.id === id
  );

  const recentJobs = useMemo(() => {
    if (!jobsData?.sparkJobs) return [];
    return jobsData.sparkJobs
      .filter((job: SparkJob) => job.sparkEngineId === id)
      .sort(
        (a: SparkJob, b: SparkJob) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .slice(0, 10);
  }, [jobsData, id]);

  // Filter out the sparkEngineId column for the recent jobs mini-table
  const recentJobsColumns = useMemo(
    () =>
      createSparkJobsTableColumns(workspaceId, currentProjectId).filter(
        (col) => col.id !== 'sparkEngineId'
      ),
    [workspaceId, currentProjectId]
  );

  if (isEngineLoading || !engine) {
    return (
      <PanelWrapper
        header={{
          title: 'Spark Engine Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canUpdate = isAuthorized('update:spark_engine');
  const canDelete = isAuthorized('delete:spark_engine');

  const isStarted = engine.status === 'STARTED';
  const isStopped = engine.status === 'STOPPED';
  const canStart = isStopped;
  const isTransitioning =
    engine.status === 'CREATING' ||
    engine.status === 'STARTING' ||
    engine.status === 'STOPPING' ||
    engine.status === 'DELETING';

  const handleStart = async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      await dal.lakehouse.startSparkEngine(workspaceId, engine.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to start spark engine:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      await dal.lakehouse.stopSparkEngine(workspaceId, engine.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to stop spark engine:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Spark Engine Details',
          onClose,
          actions: isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {isStarted && (
                <>
                  <ButtonWithIcon
                    variant='outlined'
                    size='small'
                    onClick={() => {
                      rightPanel.setIsOpen(true, PanelSize.Full);
                      rightPanel.setComponent({
                        panel: Panels.SubmitSparkJobWizard,
                        props: {
                          workspaceId,
                          sparkEngineId: engine.id,
                          sparkEngineName: engine.name,
                          onAbort: () => rightPanel.setIsOpen(false),
                          onSubmitted: () => {
                            rightPanel.setIsOpen(false);
                            onRefresh();
                          },
                        },
                      });
                    }}
                    text='Submit Job'
                    icon='material-symbols-play-arrow-rounded'
                  />
                  <ButtonWithIcon
                    variant='outlined'
                    size='small'
                    onClick={() =>
                      window.open(
                        `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/spark-ui/live/engines/${engine.id}/connect/`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    text='Spark Connect UI'
                    icon='material-symbols-open-in-new'
                  />
                </>
              )}
              <OptionMenu
                options={[
                  {
                    id: 'start-engine',
                    text: 'Start',
                    icon: 'material-symbols-play-arrow-rounded',
                    menuItemProps: {
                      disabled: !canUpdate || isTransitioning || !canStart,
                      onClick: () => void handleStart(),
                    },
                  },
                  {
                    id: 'stop-engine',
                    text: 'Stop',
                    icon: 'material-symbols-stop-rounded',
                    menuItemProps: {
                      disabled: !canUpdate || isTransitioning || !isStarted,
                      onClick: () => void handleStop(),
                    },
                  },
                  {
                    id: 'divider',
                    divider: true,
                  },
                  {
                    id: 'delete-engine',
                    text: 'Delete',
                    icon: 'material-symbols-delete-outline-rounded',
                    menuItemProps: {
                      disabled: !canDelete || isTransitioning,
                      onClick: () => setDeleteDialogOpen(true),
                    },
                  },
                ]}
              />
            </>
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px' divider={<Divider />}>
          {/* Engine Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={engine.id} />
            <DetailRow label='Name' value={engine.name} />
            <DetailRow
              label='Eon Spark Version'
              value={eonSparkVersionLabel(engine.eonSparkVersion)}
            />
            <DetailRow
              label='Status'
              value={<SparkEngineStatusCell status={engine.status} />}
            />
            <DetailRow label='Max vCPU' value={String(engine.maxVcpu)} />
            <DetailRow label='Max Memory' value={`${engine.maxMemory} GiB`} />
            <DetailRow label='Max Storage' value={`${engine.maxStorage} GB`} />
            {engine.warmCapacity && (
              <>
                <DetailRow
                  label='Warm vCPU'
                  value={String(engine.warmCapacity.vcpu)}
                />
                <DetailRow
                  label='Warm Memory'
                  value={`${engine.warmCapacity.memory} GiB`}
                />
                <DetailRow
                  label='Warm Storage'
                  value={`${engine.warmCapacity.storage} GB`}
                />
              </>
            )}
            {engine.concurrentJobs !== null &&
              engine.concurrentJobs !== undefined && (
                <DetailRow
                  label='Concurrent Jobs'
                  value={String(engine.concurrentJobs)}
                />
              )}
            {engine.queueTimeout !== null &&
              engine.queueTimeout !== undefined && (
                <DetailRow
                  label='Queue Timeout'
                  value={`${engine.queueTimeout} minutes`}
                />
              )}
            {engine.instanceFamily && (
              <DetailRow
                label='Instance Family'
                value={engine.instanceFamily}
              />
            )}
            <DetailRow
              label='Dedicated Resources'
              value={engine.dedicatedResources ? 'Yes' : 'No'}
            />
            {engine.customImage && (
              <DetailRow label='Custom Image' value={engine.customImage} />
            )}
            {engine.awsRuntimeConfig?.iamRoleArn && (
              <DetailRow
                label='IAM Role ARN'
                value={engine.awsRuntimeConfig.iamRoleArn}
              />
            )}
            <DetailRow
              label='Created'
              value={dayjs.utc(engine.createdAt).format('MMM D, YYYY HH:mm:ss')}
            />
          </Stack>

          {/* Recent Jobs */}
          <Stack gap='12px'>
            <Typography variant='subtitle2' color='text.secondary'>
              Recent Jobs
            </Typography>
            {recentJobs.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No jobs found for this engine.
              </Typography>
            ) : (
              <Stack
                sx={{
                  border: '1px solid var(--mui-palette-divider)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Table<SparkJob>
                  stickyHeader
                  entityLabel='recent jobs'
                  testId='spark-engine-recent-jobs-table'
                  showFiltersBar={false}
                  data={recentJobs}
                  isMultiSelect={false}
                  isLoading={isJobsLoading}
                  columns={recentJobsColumns}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </PanelWrapper>

      <DeleteSparkEngineDialog
        engine={engine}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '160px', flexShrink: 0 }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
